import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/Quiz.css';
import quizApi from '../services/quizApi';

const QuizResultHistory = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('current');
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchQuizHistory = useCallback(async () => {
    try {
      setLoading(true);
      const userEmail = localStorage.getItem('userEmail');
      
      if (!userEmail) {
        throw new Error('User email not found. Please log in again.');
      }

      const historyResponse = await quizApi.getUserQuizHistory(quizId, userEmail);

      // Use the latest attempt as the current result
      const latestAttempt = historyResponse.attempt_history[historyResponse.attempt_history.length - 1];
      
      setHistoryData({
        currentResult: {
          score: latestAttempt.score,
          correct_answers: latestAttempt.questions_correct,
          total_questions: latestAttempt.total_questions,
          completion_time: latestAttempt.completion_time
        },
        quiz: historyResponse.quiz,
        history: historyResponse.attempt_history,
        statistics: historyResponse.statistics
      });
    } catch (err) {
      console.error('Quiz history fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  useEffect(() => {
    document.body.classList.add("quiz-body");
    fetchQuizHistory();
    return () => {
      document.body.classList.remove("quiz-body");
    };
  }, [fetchQuizHistory]);

  const handleRetakeQuiz = () => {
    navigate(`/quiz/${quizId}`);
  };

  const handleReviewLetter = () => {
    if (historyData?.quiz?.letter_id) {
      navigate(`/prepletters?letter=${historyData.quiz.letter_id}`);
    }
  };

  if (loading) {
    return (
      <div className="quiz-state-container">
        <div className="spinner-modern"></div>
        <p className="quiz-state-text">Loading quiz results...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-container-modern">
        <div className="quiz-state-container">
          <div className="alert-modern alert-danger-modern">
            <h4>Error</h4>
            <p>{error}</p>
            <button
              className="btn-modern btn-secondary-modern mt-3"
              onClick={() => navigate('/quizzes')}
            >
              Back to Quizzes
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!historyData) {
    return (
      <div className="quiz-container-modern">
        <div className="alert-modern alert-warning-modern">No results found for this quiz.</div>
        <button
          className="btn-modern btn-primary-modern mt-3"
          onClick={() => navigate('/quizzes')}
        >
          Back to Quizzes
        </button>
      </div>
    );
  }

  return (
    <div className="quiz-container-modern">
      <div className="quiz-content-wrapper">
        <div className="quiz-results-container">
          
          {/* Header Section */}
          <div className="quiz-card mb-3">
            <div className="quiz-card-header text-center">
              <h4 className="quiz-title-modern">{historyData.quiz?.title}</h4>
              <p className="quiz-description-modern">Quiz Performance Analysis</p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="quiz-card mb-3">
            <div className="quiz-card-header">
              <div className="quiz-tabs">
                <button 
                  className={`quiz-tab ${activeTab === 'current' ? 'active' : ''}`}
                  onClick={() => setActiveTab('current')}
                >
                  Current Result
                </button>
                <button 
                  className={`quiz-tab ${activeTab === 'history' ? 'active' : ''}`}
                  onClick={() => setActiveTab('history')}
                >
                  Result History
                </button>
                <button 
                  className={`quiz-tab ${activeTab === 'share' ? 'active' : ''}`}
                  onClick={() => setActiveTab('share')}
                >
                  Share
                </button>
              </div>
            </div>
            
            <div className="quiz-card-body">
              
              {/* Current Result Tab */}
              {activeTab === 'current' && (
                <div className="current-result-section">
                  <div className="row">
                    
                    {/* Current Score Display */}
                    <div className="col-lg-6">
                      <div className="quiz-card h-100">
                        <div className="quiz-card-header">
                          <h6 className="quiz-subtitle-modern">Latest Performance</h6>
                        </div>
                        <div className="quiz-card-body text-center">
                          <div className="score-display-large">
                            <div className="score-circle">
                              <span className="score-number">{Math.round(historyData.currentResult.score)}</span>
                              <span className="score-percent">%</span>
                            </div>
                            <div className="score-details mt-3">
                              <div className="correct-answers">
                                {historyData.currentResult.correct_answers} / {historyData.currentResult.total_questions} correct
                              </div>
                              <div className="completion-date">
                                {new Date(historyData.currentResult.completion_time).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Comparison with Average */}
                    <div className="col-lg-6">
                      <div className="quiz-card h-100">
                        <div className="quiz-card-header">
                          <h6 className="quiz-subtitle-modern">vs Average Performance</h6>
                        </div>
                        <div className="quiz-card-body">
                          <div className="comparison-stats">
                            <div className="stat-row">
                              <span className="stat-label">Your Score</span>
                              <span className="stat-value text-primary">{Math.round(historyData.currentResult.score)}%</span>
                            </div>
                            <div className="stat-row">
                              <span className="stat-label">Global Average</span>
                              <span className="stat-value">{Math.round(historyData.statistics.global_average)}%</span>
                            </div>
                            <div className="stat-row">
                              <span className="stat-label">Difference</span>
                              <span className={`stat-value ${historyData.currentResult.score >= historyData.statistics.global_average ? 'text-success' : 'text-warning'}`}>
                                {historyData.currentResult.score >= historyData.statistics.global_average ? '↑' : '↓'} 
                                {Math.abs(Math.round(historyData.currentResult.score - historyData.statistics.global_average))}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* History Tab */}
              {activeTab === 'history' && (
                <div className="history-section">
                  
                  {/* Progress Summary */}
                  <div className="row mb-4">
                    <div className="col-lg-4">
                      <div className="quiz-card">
                        <div className="quiz-card-body text-center">
                          <h6 className="quiz-subtitle-modern">🏆 Best Score</h6>
                          <div className="score-highlight best">{Math.round(historyData.statistics.best_score)}%</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="quiz-card">
                        <div className="quiz-card-body text-center">
                          <h6 className="quiz-subtitle-modern">📈 Improvement</h6>
                          <div className={`score-highlight ${historyData.statistics.improvement >= 0 ? 'improvement' : 'decline'}`}>
                            {historyData.statistics.improvement >= 0 ? '+' : ''}{Math.round(historyData.statistics.improvement)}%
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="quiz-card">
                        <div className="quiz-card-body text-center">
                          <h6 className="quiz-subtitle-modern">🔄 Total Attempts</h6>
                          <div className="score-highlight attempts">{historyData.statistics.total_attempts}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Progress Graph */}
                  <div className="quiz-card mb-4">
                    <div className="quiz-card-header">
                      <h6 className="quiz-subtitle-modern">Progress Over Time</h6>
                    </div>
                    <div className="quiz-card-body">
                      <div className="progress-graph">
                        <div className="stock-graph-container">
                          <svg className="stock-graph" viewBox="0 0 400 250" style={{width: '100%', height: '300px'}}>
                            {/* Grid lines */}
                            {[0, 25, 50, 75, 100].map(value => (
                              <line 
                                key={value}
                                x1="40" 
                                y1={200 - (value * 1.6)} 
                                x2="380" 
                                y2={200 - (value * 1.6)}
                                stroke="rgba(255,255,255,0.1)" 
                                strokeWidth="1"
                              />
                            ))}
                            
                            {/* Y-axis labels */}
                            {[0, 25, 50, 75, 100].map(value => (
                              <text 
                                key={value}
                                x="35" 
                                y={205 - (value * 1.6)} 
                                fill="#9ca3af" 
                                fontSize="10" 
                                textAnchor="end"
                              >
                                {value}%
                              </text>
                            ))}
                            
                            {/* Line path */}
                            <polyline
                              fill="none"
                              stroke="#3b82f6"
                              strokeWidth="2"
                              points={historyData.history.map((attempt, index) => {
                                const x = 50 + (index * (320 / Math.max(historyData.history.length - 1, 1)));
                                const y = 200 - (Math.min(attempt.score, 100) * 1.6);
                                return `${x},${y}`;
                              }).join(' ')}
                            />
                            
                            {/* Data points with arrows */}
                            {historyData.history.map((attempt, index) => {
                              const x = 50 + (index * (320 / Math.max(historyData.history.length - 1, 1)));
                              const y = 200 - (Math.min(attempt.score, 100) * 1.6);
                              const prevScore = index > 0 ? historyData.history[index - 1].score : attempt.score;
                              const isImprovement = attempt.score > prevScore;
                              const isDecline = attempt.score < prevScore;
                              
                              return (
                                <g key={attempt.attempt_number}>
                                  {/* Data point */}
                                  <circle
                                    cx={x}
                                    cy={y}
                                    r="4"
                                    fill={attempt.score >= 80 ? '#10b981' : attempt.score >= 60 ? '#f59e0b' : '#ef4444'}
                                    stroke="#fff"
                                    strokeWidth="2"
                                  />
                                  
                                  {/* Trend arrows */}
                                  {index > 0 && isImprovement && (
                                    <polygon
                                      points={`${x-3},${y-8} ${x+3},${y-8} ${x},${y-12}`}
                                      fill="#10b981"
                                    />
                                  )}
                                  {index > 0 && isDecline && (
                                    <polygon
                                      points={`${x-3},${y+8} ${x+3},${y+8} ${x},${y+12}`}
                                      fill="#ef4444"
                                    />
                                  )}
                                  
                                  {/* Tooltip on hover */}
                                  <title>{new Date(attempt.completion_time).toLocaleDateString()}: {Math.round(attempt.score)}%</title>
                                </g>
                              );
                            })}
                            
                            {/* X-axis labels */}
                            {historyData.history.map((attempt, index) => {
                              const x = 50 + (index * (320 / Math.max(historyData.history.length - 1, 1)));
                              return (
                                <text 
                                  key={attempt.attempt_number}
                                  x={x} 
                                  y="220" 
                                  fill="#9ca3af" 
                                  fontSize="9" 
                                  textAnchor="middle"
                                >
                                  {new Date(attempt.completion_time).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})}
                                </text>
                              );
                            })}
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Best Attempt Analysis */}
                  {historyData.best_attempt_analysis && (
                    <div className="quiz-card mb-4">
                      <div className="quiz-card-header">
                        <h6 className="quiz-subtitle-modern">🏆 Best Attempt Analysis (#{historyData.best_attempt_analysis.attempt_number})</h6>
                      </div>
                      <div className="quiz-card-body">
                        <div className="skill-areas-grid">
                          {historyData.best_attempt_analysis.question_analysis.map((question, index) => (
                            <div 
                              key={question.question_id} 
                              className={`skill-area-item ${question.correct ? 'correct' : 'incorrect'}`}
                            >
                              <div className="skill-area-header">
                                <span className="question-number">Q{index + 1}</span>
                                <span className={`status-icon ${question.correct ? 'correct' : 'incorrect'}`}>
                                  {question.correct ? '✅' : '❌'}
                                </span>
                              </div>
                              <div className="skill-area-content">
                                <div className="skill-area-name">{question.skill_area}</div>
                                <div className="topic-name">{question.topic}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="skill-summary">
                          <div className="skill-stats">
                            <span className="correct-count">
                              ✅ {historyData.best_attempt_analysis.question_analysis.filter(q => q.correct).length} Correct
                            </span>
                            <span className="incorrect-count">
                              ❌ {historyData.best_attempt_analysis.question_analysis.filter(q => !q.correct).length} Incorrect
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Detailed History Table */}
                  <div className="quiz-card">
                    <div className="quiz-card-header">
                      <h6 className="quiz-subtitle-modern">Attempt Details</h6>
                    </div>
                    <div className="quiz-card-body">
                      <div className="history-table">
                        <div className="history-table-header">
                          <div className="attempt-info-header">Attempt</div>
                          <div className="attempt-score-header">Score</div>
                          <div className="attempt-details-header">Details</div>
                          <div className="attempt-time-header">Time Taken</div>
                        </div>
                        {historyData.history.map((attempt) => (
                          <div key={attempt.attempt_number} className="history-row">
                            <div className="attempt-info">
                              <span className="attempt-number">#{attempt.attempt_number}</span>
                              <span className="attempt-date">
                                {new Date(attempt.completion_time).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="attempt-score">
                              <span className={`score-badge ${attempt.score >= 80 ? 'excellent' : attempt.score >= 60 ? 'good' : 'needs-work'}`}>
                                {Math.round(attempt.score)}%
                              </span>
                            </div>
                            <div className="attempt-details">
                              <span className="questions-correct">
                                {attempt.questions_correct}/{attempt.total_questions} correct
                              </span>
                            </div>
                            <div className="attempt-time">
                              <span className="time-taken">
                                ⏱️ {attempt.time_taken ? 
                                  `${Math.floor(attempt.time_taken / 60)}m ${attempt.time_taken % 60}s` : 
                                  '10m 0s'
                                }
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Share Tab */}
              {activeTab === 'share' && (
                <div className="share-section-quiz-results">
                  <div className="share-card-quiz-results" id="share-card">
                    <div className="share-card-inner-qr">
                      <div className="share-card-logo-qr">Prep<span>Letter</span></div>
                      <div className="share-card-tagline-qr">Daily Interview Prep</div>
                      <div className="share-card-divider-qr"></div>
                      <div className="share-card-quiz-qr">
                        {historyData.quiz?.title || `Quiz`}
                      </div>
                      <div className="share-card-score-qr">{Math.round(historyData.currentResult.score)}%</div>
                      <div className="share-card-detail-qr">
                        {historyData.currentResult.correct_answers}/{historyData.currentResult.total_questions} correct
                      </div>
                      <div className="share-card-divider-qr"></div>
                      <div className="share-card-footer-qr">
                        <span className="share-card-url-qr">prepletter.app</span>
                      </div>
                    </div>
                  </div>
                  <div className="share-buttons-qr">
                    <button
                      className="share-btn-qr share-linkedin-qr"
                      onClick={() => {
                        const title = historyData.quiz?.title || '';
                        const score = Math.round(historyData.currentResult.score);
                        const emoji = score >= 80 ? '🟢' : score >= 60 ? '🟡' : '🔴';
                        const text = encodeURIComponent(
                          `${emoji} PrepLetter Quiz Result\n━━━━━━━━━━━━━━━━━━\n📚 ${title}\n🎯 Score: ${score}% (${historyData.currentResult.correct_answers}/${historyData.currentResult.total_questions} correct)\n━━━━━━━━━━━━━━━━━━\nPrepLetter helps you master coding interviews with daily practice letters and interactive quizzes.\n\n💻 Try it free → prepletter.app/learn\n\n#CodingInterview #LeetCode #TechJobs #Programming #SoftwareEngineering`
                        );
                        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=https://prepletter.app/learn&summary=${text}`, '_blank');
                      }}
                    >
                      <i className="bi bi-linkedin"></i> Share on LinkedIn
                    </button>
                    <button
                      className="share-btn-qr share-copy-qr"
                      onClick={() => {
                        const title = historyData.quiz?.title || '';
                        const score = Math.round(historyData.currentResult.score);
                        const emoji = score >= 80 ? '🟢' : score >= 60 ? '🟡' : '🔴';
                        const text = `${emoji} PrepLetter Quiz Result\n━━━━━━━━━━━━━━━━━━\n📚 ${title}\n🎯 Score: ${score}% (${historyData.currentResult.correct_answers}/${historyData.currentResult.total_questions} correct)\n━━━━━━━━━━━━━━━━━━\n💻 Try it free → prepletter.app/learn`;
                        navigator.clipboard.writeText(text);
                        alert('Copied to clipboard!');
                      }}
                    >
                      <i className="bi bi-clipboard"></i> Copy Link
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="quiz-card">
            <div className="quiz-card-body text-center">
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <button 
                  className="btn-modern btn-primary-modern btn-sm"
                  onClick={handleRetakeQuiz}
                  style={{ fontSize: '0.9rem', padding: '8px 16px' }}
                >
                  <i className="bi bi-arrow-clockwise"></i> Retake
                </button>
                
                {historyData.quiz?.quiz_type === 'daily_letter' && historyData.quiz?.letter_id && (
                  <button 
                    className="btn-modern btn-info-modern btn-sm"
                    onClick={handleReviewLetter}
                    style={{ fontSize: '0.9rem', padding: '8px 16px' }}
                  >
                    <i className="bi bi-envelope-open"></i> Review Letter
                  </button>
                )}
                
                <button 
                  className="btn-modern btn-secondary-modern btn-sm"
                  onClick={() => navigate('/quizzes')}
                  style={{ fontSize: '0.9rem', padding: '8px 16px' }}
                >
                  <i className="bi bi-arrow-left"></i> Back
                </button>
              </div>
              {/* Bug Report Link */}
              <div className="prepletters-bug-report mt-4 text-center">
                <a
                  href={`/bug-report?page=quizhistory&entity=quiz-history-${quizId}`}
                  className="bug-report-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  🐛 Report a Bug
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizResultHistory;
